import type { FunctionComponent } from 'react';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Analytics } from '@trello/atlassian-analytics';
import { localizeCount } from '@trello/legacy-i18n';

import { PowerUpsList } from './PowerUpsList';
import { createSearchIndex } from './SearchHelper';
import type { Plugins } from './types';

import styles from './SearchPage.less';

// eslint-disable-next-line @trello/assets-alongside-implementation
import emptySearchImage from 'resources/images/directory/empty-search.svg';

interface SearchPageProps {
  plugins: Plugins;
  query: string;
}

export const SearchPage: FunctionComponent<SearchPageProps> = ({
  plugins,
  query,
}) => {
  const [searchResults, setSearchResults] = useState<Plugins>([]);
  const searchIndex = createSearchIndex(plugins);

  useEffect(() => {
    const decodedQuery = decodeURIComponent(query);
    const results = searchIndex.search(decodedQuery);
    setSearchResults(results);

    Analytics.sendTrackEvent({
      action: 'completed',
      actionSubject: 'search',
      attributes: {
        totalListed: results.length,
      },
      source: 'publicDirectorySearchResultsScreen',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  return searchResults?.length !== 0 ? (
    <PowerUpsList
      title={localizeCount('count-results-for', searchResults?.length, {
        query,
      })}
      plugins={searchResults || []}
    />
  ) : (
    <div className={styles.directorySearchEmptyContainer}>
      <FormattedMessage
        tagName="h2"
        id="templates.directory.results-for"
        defaultMessage='Results for "{query}"'
        description="Results for query"
        values={{ query }}
      />
      <div className={styles.directorySearchNoResults}>
        <img
          alt=""
          role="presentation"
          className={styles.emptySearchImage}
          src={emptySearchImage}
        />
        <FormattedMessage
          tagName="h3"
          id="templates.directory.we-didnt-find-any-power-ups-matching-your-search"
          defaultMessage="We didn't find any Power-Ups matching your search."
          description="Message when query does not match any power-up"
        />
        <FormattedMessage
          tagName="p"
          id="templates.directory.try-another-search-or-build-it-yourself"
          defaultMessage="Try another search or <a>build it yourself</a>!"
          description="Try another search or build it yourself"
          values={{
            a: (chunks) => (
              <a
                href="https://developers.trello.com/docs/get-started"
                target="_blank"
              >
                {chunks}
              </a>
            ),
          }}
        />
      </div>
    </div>
  );
};
